import React from "react";
import { Box, Grid } from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import BreadcrumbHeader from "../../layout/BreadcrumbsHeader";
import ThemeBGWrapper from "../../ui-components/ThemeBGWrapper";
import DashboardAnalytics from "./components/DashboardAnalytics";
import { readVariant, writeVariant } from "./variants/VariantSwitcher";

// The page is now just a frame around the analytics section.
//
// What used to live here: four stat cards whose all-time counts duplicated the
// section below (two of them sharing the label "Registered Today"), plus a
// branch behind `const dashboardVisibility = false` that could never render —
// a hardcoded users table, fake monthly charts, and the delete-user flow that
// already exists under /settings. The four cards' figures now ride in each KPI
// tile's footnote, drawn from the same population as every other number here,
// so the page can no longer show two different startup counts.
// The wrapper is position: fixed, so its height comes from the viewport and
// never from this page's content. That makes the scroll container's height the
// only thing standing between an admin and the bottom of the page, and it has
// to be exact: `calc(100% - 2rem)` was measured against the WHOLE wrapper and
// subtracted a guess, ignoring the 68px breadcrumb header above it. The
// scroller then believed its window was 58px taller than the space it actually
// occupied, and stopped scrolling 58px short — with the campaign table in it.
//
// So: size the wrapper to the area that is actually on screen (it starts 20px
// down, hence the same 1.25rem its own minHeight already used), lay it out as a
// column, and let the scroller take whatever the header leaves. No magic
// numbers, and it stays correct as the header or the content changes.
// ONE header, the same one, whatever is below it.
//
// The title, the meta line, the Design switcher and the range control are the
// page's furniture: they do not belong to a design direction, and an admin
// comparing A against C should not also be re-learning where the filters live.
// So the frame always draws the breadcrumb and the variants draw no header of
// their own — a variant restyles the content under the header, nothing above
// it.
//
// The variant choice still lives here because this is the component that owns
// the frame and passes it down.
const Dashboard = () => {
  const [variant, setVariant] = React.useState(readVariant);
  // PerfectScrollbar, not the window, owns this page's scrolling — the wrapper
  // is position: fixed and overflow: hidden — so window.scrollTo does nothing
  // here. This is the element that actually moves.
  const scrollerRef = React.useRef(null);

  const chooseVariant = (key) => {
    setVariant(key);
    writeVariant(key);
  };

  // Switching a variant or a range replaces everything below the header. A
  // reader halfway down the page would otherwise land mid-section in a layout
  // they have not seen, with the control they just used scrolled off.
  const resetView = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let smooth = true;
    try {
      smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      smooth = true;
    }
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    } else {
      el.scrollTop = 0;
    }
  }, []);

  return (
    <ThemeBGWrapper
      sx={{
        // The wrapper is offset 20px from the top on desktop and flush on
        // anything below md, so the height it can actually show differs by that
        // much — mirrored here from ThemeBGWrapper's own `top`.
        height: { xs: "100dvh", md: "calc(100dvh - 1.25rem)" },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >

        <BreadcrumbHeader title="Dashboard" sx={{ paddingBottom: "0.5rem", flex: "0 0 auto" }} />
      <PerfectScrollbar
        containerRef={(ref) => { scrollerRef.current = ref; }}
        style={{ flex: "1 1 auto", minHeight: 0 }}
      >
        <Grid container>
          {/* 32px a side is a desktop gutter; on a 360px phone it spends 64px
              of 360 on nothing. The sticky header in DashboardAnalytics bleeds
              against these exact values, so the two move together. */}
          <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 2, md: 4 }, width: "100%", minWidth: 0 }}>
            <DashboardAnalytics
              variant={variant}
              onVariantChange={chooseVariant}
              onResetView={resetView}
            />
          </Box>
        </Grid>
      </PerfectScrollbar>
    </ThemeBGWrapper>
  );
};

export default Dashboard;
